// src/components/organisms/GreetingsModal/GreetingsModal.tsx
import { useEffect, useState } from 'react';
import { Modal } from 'components/organisms/Modal';
import { Loader } from 'legacy-components/ui/loader/loader';
import { Button } from 'legacy-components/componets';
import { useWindowSize } from 'hooks';
import { useNavigate } from 'react-router-dom';
import { Facebook, Icon, Instagram, Linkedin, Logo, Twitter } from 'legacy-components/componets';
import { useUserByInvitationToken } from 'hooks/query/user/use-user-by-invitation-token';
import { useGreetingsModal } from 'context/GreetingsModalProvider';

interface GreetingsModalProps {
    onClose: (navigateToOriginalPath?: boolean) => void;
    registrationToken?: string;
    phoneNumber?: string;
}

const GreetingsModal = ({ onClose, registrationToken, phoneNumber: propPhoneNumber }: GreetingsModalProps) => {
    const { updatePhoneNumber } = useGreetingsModal();
    const { isMobile } = useWindowSize();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(true);

    const { data: userData, isLoading, isError } = useUserByInvitationToken(registrationToken);

    // Update the phone number in the provider when the API call succeeds
    useEffect(() => {
        if (userData?.phoneNumber) {
            updatePhoneNumber(userData.phoneNumber);
        }
    }, [userData, updatePhoneNumber]);

    useEffect(() => {
        // Ensure modal is open when component mounts
        setIsOpen(true);
    }, []);

    const handleClose = (navigateToOriginalPath: boolean = true) => {
        setIsOpen(false);
        // Give the modal time to animate out before calling onClose
        setTimeout(() => onClose(navigateToOriginalPath), 300);
    };

    if (isLoading) {
        return (
            <Modal
                isOpen={isOpen}
                onOpenChange={() => handleClose(false)}
                title="Loading..."
                contentClassName="w-full max-w-md z-[1000]"
            >
                <div className="flex justify-center p-8">
                    <Loader />
                </div>
            </Modal>
        );
    }

    if (isError || !userData) {
        return (
            <Modal
                isOpen={isOpen}
                onOpenChange={() => handleClose(false)}
                title="Error"
                contentClassName="w-full max-w-md z-[100]"
            >
                <div className="p-6 text-center">
                    <p className="mb-6">Failed to load invitation details. The link may be invalid or expired.</p>
                    <div className="flex justify-center gap-4">
                        <Button
                            label="Go back"
                            theme="primary"
                            onClick={() => {
                                handleClose(false);
                                navigate('/');
                            }}
                        />
                    </div>
                </div>
            </Modal>
        );
    }

    const { userName, triggerType, firstName, phoneNumber: userPhoneNumber } = userData;

    return (
        <Modal
            titleClassName='bg-[#66CCFF]'
            title={<span className='bg-[#66CCFF] text-[#FFFFFF] text-[22px]'>Welcome to Axxelist!</span>}
            isOpen={isOpen}
            isFullScreen={isMobile}
            contentClassName={'w-[670px] py-0 sm:py-0 px-0 sm:px-0 z-[100]'}
            innerContentClassName={'py-4 sm:pb-[30px] sm:pt-[10px] px-3 sm:px-6'}
            onOpenChange={(open) => !open && handleClose()}
        >
            <div>
                <div className={`text-center mb-7 text-[16px] font-bold`}>
                    Let's AXXELerate your rental experience with excellence and confidence!
                </div>
                <div className="text-left mb-5">
                    <p>Dear {firstName || 'Valued Property Owner'},</p>
                    <p className="mt-2">
                        You're receiving this invitation to register at Axxelist's platform because a renter using the Axxelist platform has expressed interest in
                        {triggerType === 1 && " scheduling a tour on"}
                        {triggerType === 0 && " making an offer on"}
                        {" your property."}
                    </p>
                </div>
                <div className="text-left mb-5">
                    <p>By creating a free account, you can view the offer details, respond to your requests, and explore all the powerful tools Axxelist offers to help you manage and promote your rental listings with ease. We're excited to have you join our community of property owners and managers!</p>
                </div>
                <div className="text-left mb-5">
                    <p>The Axxelist Team</p>
                </div>

                <div className='flex flex-col gap-4 mt-7 items-center'>
                    <Button
                        className='w-full sm:w-auto'
                        label='Register and Start Listing'
                        theme={'primary'}
                        onClick={() => {
                            // Pass true to indicate we want to navigate to sign-up
                            onClose(true);
                        }}
                    />

                    <div className="mb-1 bg-[#F1FBFF] px-10 py-3 rounded-md flex justify-center items-center h-8">
                        <div className='flex space-x-6'>
                            <Facebook color='#66CCFF' height={24} width={24} />
                            <Twitter color='#66CCFF' height={24} width={24} />
                            <Instagram color='#66CCFF' height={24} width={24} />
                            <Linkedin color='#66CCFF' height={24} width={24} />
                        </div>
                    </div>
                    <div className="text-left mb-1">
                        <div className='mb-1 md:mb-0'>
                            <Logo className={'w-[100px] h-[20px]'} />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default GreetingsModal;